import type { RenderTooltipContentData } from '@shopify/polaris-viz';
import type { EMetricKey } from '~/types/metric';

import { IncreasePercentage, isReversePageAnalyticMetric } from '~/modules/analytics';
import { Card, BlockStack, InlineStack, Text } from '@shopify/polaris';
import { PLACEHOLDER_VALUE } from '~/modules/page-analytic/constants';

export interface PALineChartTooltipProps {
  data: RenderTooltipContentData;
  formatValue: (value: number) => string;
  metricKey?: EMetricKey;
}
export const PALineChartTooltip: React.FC<PALineChartTooltipProps> = ({
  data,
  formatValue,
  metricKey,
}) => {
  const { activeIndex } = data;
  const currentData = data?.dataSeries[0]?.data[activeIndex] as any;
  const previousData = data?.dataSeries[1]?.data[activeIndex] as any;

  const formatPercent = () => {
    const value = currentData.trend?.value;
    if (!value) return PLACEHOLDER_VALUE;

    const valueNumber = Number(value.replace(/[%~]/g, ''));

    if (currentData.trend?.trend === 'negative') {
      return valueNumber * -1;
    }

    return valueNumber;
  };

  return (
    <div className="min-w-[175px] w-fit">
      <Card padding={'200'}>
        <BlockStack gap={'100'}>
          <Text as="p" variant="bodySm" fontWeight="semibold">
            {data.formatters?.titleFormatter?.(data.title || '') || data.title}
          </Text>
          <BlockStack gap={'100'}>
            <InlineStack gap={'400'} align="space-between" blockAlign="center">
              <InlineStack gap={'100'} blockAlign="center">
                <div className="bg-[#4FA9EA] w-[12px] rounded-[10px] h-[2px]"></div>
                <Text as="p" variant="bodySm" fontWeight="medium" tone="subdued">
                  {currentData.tooltipKey}
                </Text>
              </InlineStack>
              <InlineStack blockAlign="center" gap="100">
                <Text as="span" variant="bodySm" fontWeight="semibold">
                  {formatValue(currentData.value)}
                </Text>
                <IncreasePercentage
                  increase={formatPercent()}
                  isReverse={isReversePageAnalyticMetric(metricKey)}
                />
              </InlineStack>
            </InlineStack>

            <InlineStack gap={'400'} align="space-between" blockAlign="center">
              <InlineStack gap={'100'} blockAlign="center">
                <div className="border border-[#A1CAE7] w-[12px] border-dashed" />
                <Text as="p" variant="bodySm" tone="subdued" fontWeight="medium">
                  {previousData.tooltipKey}
                </Text>
              </InlineStack>
              <InlineStack blockAlign="center" gap="100">
                <Text as="span" variant="bodySm" fontWeight="semibold">
                  {formatValue(previousData.value)}
                </Text>
                <div className="opacity-0">
                  <IncreasePercentage
                    increase={formatPercent()}
                    isReverse={isReversePageAnalyticMetric(metricKey)}
                  />
                </div>
              </InlineStack>
            </InlineStack>
          </BlockStack>
        </BlockStack>
      </Card>
    </div>
  );
};
